<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { DatalayerReadyData, UserData } from '~/interfaces/tracking'
import useUserStore from '~/store/user'
import { getLocalUrl } from '~/utils/url'

const config = useRuntimeConfig()
const APPLICATION_URL = config.public.APPLICATION_URL as string
const S3_LEGACY_URL = config.public.S3_LEGACY_URL as string
const S3_ASSETS_URL = config.public.S3_ASSETS_URL as string
const GTM_ID = config.public.GTM_ID as string
const route = useRoute()
const { userId, legacyUserData, billingAddress } = useUserStore()
const { locale } = useI18n()

const locales = ref(['de', 'en', 'es', 'fr', 'it', 'nl', 'pt'])
const isDesktop = ref(true)

const { href } = useRequestURL()

const sendDataLayerReadyEvent = () => {
    const user_data: UserData = {
        user_id: userId || 0,
        email_address: billingAddress?.email || '',
        ...(billingAddress?.countryCode && {
            address: {
                first_name: billingAddress.firstName || '',
                last_name: billingAddress.name || '',
                city: billingAddress.city,
                postal_code: billingAddress.postalCode,
                country: billingAddress.countryCode
            }
        })
    }

    const pathParts = route.path.split('/')
    const dataToSend: DatalayerReadyData = {
        event: 'datalayer_ready',
        content_group: `${pathParts[1]}-${pathParts[2]}`,
        language: locale.value,
        country: locale.value,
        ...(userId && { user_data })
    }

    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).dataLayer.push(dataToSend)
}

onMounted(() => {
    if (import.meta.client) {
        // Use this workaround as useBreakpoints doesn't seem to work in the layout
        isDesktop.value = window.innerWidth >= 768
    }

    sendDataLayerReadyEvent()
})

useSeoMeta({
    ogType: 'website',
    ogLocale: locale,
    ogLocaleAlternate: locales,
    ogImage: `${APPLICATION_URL}/meta/default.jpg`,
    ogUrl: href
})
useHead({
    script: [
        {
            type: 'text/javascript',
            innerHTML:
                `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\n` +
                `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\n` +
                `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n` +
                `'https://msr.chargemap.com/sh26cifzaticam0.js?awl='+i.replace(/^GTM-/, '')+dl;f.parentNode.insertBefore(j,f);\n` +
                `})(window,document,'script','dataLayer','${GTM_ID}')`
        }
    ],
    noscript: [
        {
            innerHTML: `<iframe src="https://msr.chargemap.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        }
    ]
})
</script>

<template>
    <div>
        <Html :lang="locale" />
        <Head>
            <Link
                rel="apple-touch-icon"
                type="image/png"
                sizes="180x180"
                :href="`${APPLICATION_URL}/meta/apple-touch-icon.png`"
            />
            <Link
                rel="mask-icon"
                color="#5bbad5"
                :href="`${APPLICATION_URL}/meta/safari-pinned-tab.svg`"
            />
            <Link rel="canonical" crossorigin="use-credentials" :href />
            <Link
                rel="manifest"
                crossorigin="use-credentials"
                :href="`${APPLICATION_URL}/meta/site.webmanifest`"
            />
            <Link rel="icon" :href="`${APPLICATION_URL}/favicon.svg`" />

            <Meta name="theme-color" content="#ffffff" />
        </Head>
        <ChargemapNavigation
            :locale
            :s3-url="S3_LEGACY_URL"
            :default-avatar-path="`${S3_ASSETS_URL}/common/default-avatar.png`"
            :img-url="`${config.public.LEGACY_URL}/medias/v2`"
            :user-id="userId || 0"
            :route="route.path"
            :user-avatar="legacyUserData?.avatar || undefined"
            :cdn-url="config.app.cdnURL || getLocalUrl()"
        />
        <div class="content-wrapper">
            <slot />
        </div>
        <ChargemapFooter
            :locale
            :cdn-url="config.app.cdnURL"
            :is-desktop
            :current-path="route.path"
        />
    </div>
</template>
